import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Globe, ChevronDown, Menu, X, Search, Navigation, Heart } from 'lucide-react';
import { SITE, LANGUAGES } from '../mock/data';
import { useLang } from '../i18n';
import { useCurrency, CURRENCY_SYMBOLS, CURRENCY_LABELS } from '../currency';
import { useFavorites } from '../favorites';
import { publicApi } from '../api';

// Toggle to show/hide currency switcher across the header (desktop + mobile).
const CURRENCY_ENABLED = false;

export default function Header() {
  const { lang, setLang } = useLang();
  const { currency, setCurrency } = useCurrency();
  const { count: favCount } = useFavorites();
  const [langOpen, setLangOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const loc = useLocation();
  const nav = useNavigate();

  // Cities in the nav are fetched dynamically from the DB so that newly
  // created cities appear automatically. `coming_soon` cities are hidden
  // (they still have their own dedicated route but shouldn't clutter the menu).
  const [cities, setCities] = useState([]);
  useEffect(() => {
    let alive = true;
    publicApi.cities()
      .then(list => {
        if (!alive) return;
        setCities(
          (list || [])
            .filter(c => c && c.slug && c.status !== 'coming_soon')
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        );
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim().length < 2) return;
    nav(`/cerca?q=${encodeURIComponent(searchQ.trim())}`);
    setSearchOpen(false);
    setMobileOpen(false);
  };

  const navLinks = [
    { to: '/', label: 'Home' },
    ...cities.map(c => ({ to: `/${c.slug}`, label: c.name || c.slug })),
  ];

  const isActive = (to) => (to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(to));

  return (
    <nav className="yup-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        {/* MOBILE LAYOUT — 3 columns: [lang-flag] [logo-centered] [icons] */}
        <div className="md:hidden grid grid-cols-3 items-center gap-2">
          {/* LEFT: language selector (flag) */}
          <div className="justify-self-start relative">
            <button
              onClick={() => { setLangOpen(o => !o); setCurrencyOpen(false); }}
              className="mobile-lang-flag-btn flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Language selector"
              data-testid="mobile-header-lang-btn"
            >
              <span className="text-2xl leading-none" aria-hidden="true">
                {LANGUAGES.find(l => l.code === lang)?.flag || '🌐'}
              </span>
              <ChevronDown size={13} className={`text-gray-500 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            {langOpen && (
              <div
                className="absolute left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg py-1 min-w-[170px] z-50"
                onMouseLeave={() => setLangOpen(false)}
              >
                {LANGUAGES.map(l => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    data-testid={`mobile-header-lang-option-${l.code}`}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 ${lang === l.code ? 'text-[color:var(--yup-primary)] font-semibold' : 'text-gray-700'}`}
                  >
                    <span className="text-xl leading-none" aria-hidden="true">{l.flag}</span>
                    <span>{l.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* CENTER: logo */}
          <Link to="/" className="justify-self-center flex items-center" data-testid="header-logo-mobile">
            <img
              src={SITE.logo}
              alt="YUP"
              className="h-11 w-auto object-contain"
              style={{ maxWidth: 'none' }}
            />
          </Link>

          {/* RIGHT: search + favorites + hamburger */}
          <div className="justify-self-end flex items-center gap-0.5">
            <button
              onClick={() => setSearchOpen(o => !o)}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-700"
              aria-label="Cerca"
              data-testid="mobile-header-search-btn"
            >
              <Search size={18} />
            </button>
            <Link
              to="/preferiti"
              className="relative inline-flex p-2 rounded-md hover:bg-gray-100 text-gray-700"
              aria-label="Preferiti"
              data-testid="mobile-header-favorites-btn"
            >
              <Heart size={18} />
              {favCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--yup-primary)] text-black text-[10px] font-bold flex items-center justify-center">
                  {favCount > 99 ? '99+' : favCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setMobileOpen(o => !o)}
              className="p-2 rounded-md hover:bg-gray-100"
              aria-label="Menu"
              data-testid="header-hamburger-btn"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* DESKTOP LAYOUT — unchanged from previous version */}
        <div className="hidden md:flex items-center justify-between gap-3">
          {/* LOGO */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="header-logo">
            <img
              src={SITE.logo}
              alt="YUP"
              className="h-14 w-auto object-contain"
              style={{ maxWidth: 'none' }}
            />
          </Link>

          {/* NAV */}
          <div className="flex items-center gap-8">
            {navLinks.map(l => (
              <Link
                key={l.to}
                to={l.to}
                className={`text-sm font-medium tracking-wider uppercase transition-colors ${isActive(l.to) ? 'text-[color:var(--yup-primary)]' : 'text-gray-700 hover:text-[color:var(--yup-primary)]'}`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/promuoviti"
              className={`text-sm font-medium tracking-wider uppercase transition-colors ${isActive('/promuoviti') ? 'text-[color:var(--yup-primary)]' : 'text-[color:var(--yup-primary)]/85 hover:text-[color:var(--yup-primary)]'}`}
              data-testid="header-promote-btn"
            >
              Promuoviti
            </Link>
          </div>

          {/* RIGHT SIDE (desktop) */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setSearchOpen(o => !o)}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-700"
              aria-label="Cerca"
              data-testid="header-search-btn"
            >
              <Search size={18} />
            </button>
            <Link
              to="/preferiti"
              className="relative inline-flex p-2 rounded-md hover:bg-gray-100 text-gray-700"
              aria-label="Preferiti"
              title="I miei preferiti"
              data-testid="header-favorites-btn"
            >
              <Heart size={18} />
              {favCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--yup-primary)] text-black text-[10px] font-bold flex items-center justify-center"
                  data-testid="header-favorites-count"
                >
                  {favCount > 99 ? '99+' : favCount}
                </span>
              )}
            </Link>
            <Link
              to="/vicino-a-me"
              className="inline-flex p-2 rounded-md hover:bg-gray-100 text-gray-700"
              aria-label="Vicino a me"
              data-testid="header-nearby-btn"
              title="Trova attività vicino a te"
            >
              <Navigation size={18} />
            </Link>
            {CURRENCY_ENABLED && (
              <div className="relative">
                <button
                  onClick={() => { setCurrencyOpen(o => !o); setLangOpen(false); }}
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-md hover:bg-gray-100 text-sm font-semibold text-gray-700 transition-colors"
                  aria-label="Currency selector"
                  data-testid="header-currency-btn"
                >
                  <span>{CURRENCY_SYMBOLS[currency] || '€'}</span>
                  <span>{currency}</span>
                  <ChevronDown size={14} className={`transition-transform ${currencyOpen ? 'rotate-180' : ''}`} />
                </button>
                {currencyOpen && (
                  <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg py-1 min-w-[180px] z-50" onMouseLeave={() => setCurrencyOpen(false)}>
                    {Object.keys(CURRENCY_SYMBOLS).map(code => (
                      <button
                        key={code}
                        onClick={() => { setCurrency(code); setCurrencyOpen(false); }}
                        data-testid={`currency-option-${code}`}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${currency === code ? 'text-[color:var(--yup-primary)] font-semibold' : 'text-gray-700'}`}
                      >
                        <span className="mr-2">{CURRENCY_SYMBOLS[code]}</span>{code} — {CURRENCY_LABELS[code]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="relative">
              <button
                onClick={() => { setLangOpen(o => !o); setCurrencyOpen(false); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md hover:bg-gray-100 text-sm font-semibold text-gray-700 transition-colors"
                aria-label="Language selector"
                data-testid="header-lang-btn"
              >
                <Globe size={16} />
                <span>{lang.toUpperCase()}</span>
                <ChevronDown size={14} className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg py-1 min-w-[150px] z-50" onMouseLeave={() => setLangOpen(false)}>
                  {LANGUAGES.map(l => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 ${lang === l.code ? 'text-[color:var(--yup-primary)] font-semibold' : 'text-gray-700'}`}
                    >
                      <span className="text-lg leading-none" aria-hidden="true">{l.flag}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {searchOpen && (
        <form onSubmit={submitSearch} className="border-t border-gray-200 bg-white">
          <div className="max-w-4xl mx-auto px-6 py-3 flex gap-2">
            <input
              autoFocus
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Cerca attività, categorie, indirizzi..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--yup-primary)]"
              data-testid="header-search-input"
            />
            <button type="submit" className="px-4 py-2 bg-[color:var(--yup-primary)] hover:brightness-110 text-white rounded-md text-sm font-medium"
              data-testid="header-search-submit">
              Cerca
            </button>
          </div>
        </form>
      )}

      {mobileOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-5 flex flex-col gap-4 max-h-[calc(100vh-70px)] overflow-y-auto">
            {/* NAV LINKS */}
            <div className="flex flex-col gap-3 pb-4 border-b border-gray-100">
              {navLinks.map(l => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  className={`text-base font-medium tracking-wider uppercase ${isActive(l.to) ? 'text-[color:var(--yup-primary)]' : 'text-gray-700'}`}
                  data-testid={`mobile-nav-${l.to.replace('/', '') || 'home'}`}
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/promuoviti"
                onClick={() => setMobileOpen(false)}
                className="text-base font-bold tracking-wider uppercase text-[color:var(--yup-primary)]"
                data-testid="mobile-nav-promuoviti"
              >
                Promuoviti
              </Link>
            </div>

            {/* UTILITY LINKS */}
            <div className="flex flex-col gap-3 pb-4 border-b border-gray-100">
              <Link
                to="/vicino-a-me"
                onClick={() => setMobileOpen(false)}
                className={`inline-flex items-center gap-2.5 text-sm font-medium tracking-wider uppercase ${isActive('/vicino-a-me') ? 'text-[color:var(--yup-primary)]' : 'text-gray-700'}`}
                data-testid="mobile-nearby-link"
              >
                <Navigation size={18} /> Vicino a me
              </Link>
              <Link
                to="/preferiti"
                onClick={() => setMobileOpen(false)}
                className={`inline-flex items-center gap-2.5 text-sm font-medium tracking-wider uppercase ${isActive('/preferiti') ? 'text-[color:var(--yup-primary)]' : 'text-gray-700'}`}
                data-testid="mobile-favorites-link"
              >
                <Heart size={18} /> Preferiti {favCount > 0 && <span className="text-[color:var(--yup-primary)] font-bold">({favCount})</span>}
              </Link>
            </div>

            {/* CURRENCY SELECTOR — HIDDEN (see CURRENCY_ENABLED flag at top of file) */}
            {CURRENCY_ENABLED && (
            <div className="pb-4 border-b border-gray-100">
              <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">Valuta</div>
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(CURRENCY_SYMBOLS).map(code => (
                  <button
                    key={code}
                    onClick={() => setCurrency(code)}
                    data-testid={`mobile-currency-${code}`}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-semibold border transition ${
                      currency === code
                        ? 'bg-[color:var(--yup-primary)]/10 border-[color:var(--yup-primary)] text-[color:var(--yup-primary)]'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-base">{CURRENCY_SYMBOLS[code]}</span>
                      <span>{code}</span>
                    </span>
                    <span className="text-[10px] text-gray-400 truncate ml-2">{CURRENCY_LABELS[code]}</span>
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* LANGUAGE SELECTOR — moved to top-left flag on mobile; kept only there to avoid duplication */}
          </div>
        </div>
      )}
    </nav>
  );
}
